import { useEffect, useMemo, useState } from 'react';
import { Breadcrumb, Button, Empty, Spin, message } from 'antd';
import { ExpandOutlined, FullscreenExitOutlined, HomeOutlined, ReloadOutlined } from '@ant-design/icons';
import screenfull from 'screenfull';
import api from '../api';
import './DataScreenPage.css';

const buildingNames = ['A栋', 'B栋', 'C栋', 'D栋'];

const DataScreenPage = () => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [dormitories, setDormitories] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadDormitories = async () => {
    setLoading(true);
    try {
      const response = await api.get('/accommodation/dormitories');
      setDormitories(response.data || []);
    } catch {
      message.error('获取寝室统计数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDormitories();
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(Boolean(screenfull.isFullscreen));
    screenfull.on('change', handleFullscreenChange);
    return () => screenfull.off('change', handleFullscreenChange);
  }, []);

  const statistics = useMemo(() => {
    const getBeds = dormitory => Number(dormitory.bedCapacity) || 0;
    const getOccupiedBeds = dormitory => Number.isFinite(Number(dormitory.occupiedBeds))
      ? Number(dormitory.occupiedBeds)
      : Array.isArray(dormitory.residents) ? dormitory.residents.length : 0;
    const isMale = dormitory => dormitory.genderType === '男生寝室';
    const isFemale = dormitory => dormitory.genderType === '女生寝室';
    const isLocked = dormitory => Boolean(dormitory.locked);
    const isInBuilding = (dormitory, building) => String(dormitory.building || '').replace(/\s/g, '').replace(/栋$/, '') === building.replace(/栋$/, '');
    const roomTypes = [...new Set(dormitories.map(getBeds).filter(Boolean))]
      .sort((left, right) => left - right);
    const buildingStats = buildingNames.map(building => {
      const list = dormitories.filter(dormitory => isInBuilding(dormitory, building));
      return {
        building,
        rooms: list.length,
        beds: list.reduce((total, dormitory) => total + getBeds(dormitory), 0),
        usedRooms: list.filter(dormitory => getOccupiedBeds(dormitory) > 0).length,
        emptyRooms: list.filter(dormitory => getOccupiedBeds(dormitory) === 0).length,
        usedBeds: list.reduce((total, dormitory) => total + getOccupiedBeds(dormitory), 0),
        emptyBeds: list.reduce((total, dormitory) => total + Math.max(0, getBeds(dormitory) - getOccupiedBeds(dormitory)), 0),
        lockedRooms: list.filter(isLocked).length,
        lockedBeds: list.filter(isLocked).reduce((total, dormitory) => total + getBeds(dormitory), 0),
        roomTypes: roomTypes.filter(capacity => list.some(dormitory => getBeds(dormitory) === capacity)).map(capacity => {
          const roomList = list.filter(dormitory => getBeds(dormitory) === capacity);
          const rooms = roomList.length;
          const usedRooms = roomList.filter(dormitory => getOccupiedBeds(dormitory) > 0).length;
          const usedBeds = roomList.reduce((total, dormitory) => total + getOccupiedBeds(dormitory), 0);
          return {
            capacity,
            rooms,
            beds: rooms * capacity,
            usedRooms,
            emptyRooms: rooms - usedRooms,
            usedBeds,
            emptyBeds: roomList.reduce((total, dormitory) => total + Math.max(0, getBeds(dormitory) - getOccupiedBeds(dormitory)), 0),
          };
        }),
      };
    });
    return {
      totalRooms: dormitories.length,
      maleRooms: dormitories.filter(isMale).length,
      femaleRooms: dormitories.filter(isFemale).length,
      totalBeds: dormitories.reduce((total, dormitory) => total + getBeds(dormitory), 0),
      maleBeds: dormitories.filter(isMale).reduce((total, dormitory) => total + getBeds(dormitory), 0),
      femaleBeds: dormitories.filter(isFemale).reduce((total, dormitory) => total + getBeds(dormitory), 0),
      lockedRooms: dormitories.filter(isLocked).length,
      lockedBeds: dormitories.filter(isLocked).reduce((total, dormitory) => total + getBeds(dormitory), 0),
      buildingStats,
    };
  }, [dormitories]);

  const toggleFullscreen = () => {
    if (screenfull.isEnabled) screenfull.toggle(document.getElementById('data-screen-root'));
  };

  const overviewItems = [
    { label: '总寝室数', value: statistics.totalRooms, accent: 'blue' },
    { label: '男寝总寝室数', value: statistics.maleRooms, accent: 'cyan' },
    { label: '女寝总寝室数', value: statistics.femaleRooms, accent: 'rose' },
    { label: '总床位数', value: statistics.totalBeds, accent: 'indigo' },
    { label: '男寝总床位数', value: statistics.maleBeds, accent: 'teal' },
    { label: '女寝总床位数', value: statistics.femaleBeds, accent: 'orange' },
    { label: '已锁定寝室数', value: statistics.lockedRooms, accent: 'amber' },
    { label: '锁定床位数', value: statistics.lockedBeds, accent: 'red' },
  ];

  return (
    <div id="data-screen-root" className="data-screen-page">
      {!isFullscreen && (
        <div className="page-header">
          <Breadcrumb items={[
            { title: <><HomeOutlined /> 首页</> },
            { title: '数据大屏' },
          ]} />
        </div>
      )}

      <section className="data-screen-canvas" aria-label="数据大屏">
        <header className="data-screen-toolbar">
          <div>
            <span className="data-screen-kicker">HONGREN CLOUD</span>
            <h1>住宿资源数据大屏</h1>
          </div>
          <div className="data-screen-actions">
            <Button aria-label="刷新数据" type="text" className="data-screen-fullscreen" icon={<ReloadOutlined />} loading={loading} onClick={loadDormitories} />
            <Button aria-label={isFullscreen ? '退出全屏' : '进入全屏'} type="text" className="data-screen-fullscreen" icon={isFullscreen ? <FullscreenExitOutlined /> : <ExpandOutlined />} onClick={toggleFullscreen} />
          </div>
        </header>

        <div className="data-screen-workspace">
          {loading ? <div className="data-screen-loading"><Spin size="large" /></div> : dormitories.length === 0 ? <Empty description="暂无寝室数据" /> : <>
            <section className="data-screen-section" aria-label="寝室总览">
              <div className="data-screen-section-heading"><span>寝室资源总览</span><small>数据来自寝室管理</small></div>
              <div className="data-screen-overview-grid">
                {overviewItems.map(item => <article className={'data-stat-card accent-' + item.accent} key={item.label}>
                  <span className="data-stat-label">{item.label}</span>
                  <strong className="data-stat-value">{item.value.toLocaleString()}</strong>
                  <span className="data-stat-unit">{item.label.includes('床位') ? '个床位' : '间寝室'}</span>
                </article>)}
              </div>
            </section>

            <section className="data-screen-section" aria-label="楼栋资源分布">
              <div className="data-screen-section-heading"><span>楼栋资源分布</span><small>A 栋至 D 栋</small></div>
              <div className="data-screen-building-grid">
                {statistics.buildingStats.map(item => <article className="building-stat-card" key={item.building}>
                  <div className="building-stat-title">{item.building}</div>
                  <div className="building-stat-values">
                    <div><span>楼栋寝室数</span><strong>{item.rooms.toLocaleString()}<small> 间</small></strong></div>
                    <div><span>楼栋床位数</span><strong>{item.beds.toLocaleString()}<small> 个</small></strong></div>
                  </div>
                  <div className="building-room-types">
                    {item.roomTypes.map(roomType => <div className="building-room-type" key={roomType.capacity}>
                      <span>{roomType.capacity}人寝</span><strong>{roomType.rooms}<small> 间</small></strong><em>{roomType.beds} 个床位</em><div className="room-type-occupancy"><div><span>已使用</span><strong>{roomType.usedRooms}<small>间</small> / {roomType.usedBeds}<small>床</small></strong></div><div><span>空余</span><strong>{roomType.emptyRooms}<small>间</small> / {roomType.emptyBeds}<small>床</small></strong></div></div>
                    </div>)}
                  </div>
                  <div className="building-occupancy">
                    <div><span>已使用寝室</span><strong>{item.usedRooms}<small> 间</small></strong></div>
                    <div><span>空余寝室</span><strong>{item.emptyRooms}<small> 间</small></strong></div>
                    <div><span>已使用床位</span><strong>{item.usedBeds}<small> 张</small></strong></div>
                    <div><span>空余床位</span><strong>{item.emptyBeds}<small> 张</small></strong></div>
                    <div className="building-locked-stat"><span>已锁定寝室</span><strong>{item.lockedRooms}<small> 间</small></strong></div>
                    <div className="building-locked-stat"><span>锁定床位数</span><strong>{item.lockedBeds}<small> 张</small></strong></div>
                  </div>
                </article>)}
              </div>
            </section>
          </>}
        </div>
      </section>
    </div>
  );
};

export default DataScreenPage;
